import React, { useState, useEffect, useRef, useCallback } from 'react';
import IconButton from './IconButton';
import InputText from './InputText';
import InputTextArea from './InputTextArea';
import SaveStatusFeedback, { SaveStatus } from './SaveStatusFeedback';
import MenuButton from './MenuButton';
import InputTodoTags from './InputTodoTags';
import { HabitEntity } from '../types/data';
import { useTags } from '../hooks/useTags';
import { isShoppingHabitId } from '../utils/habits';
import './DrawerHabitDetail.css';

interface DrawerHabitDetailProps {
  habit?: HabitEntity;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (habit: HabitEntity) => void;
  onDelete?: (habitId: string) => void;
}

const DrawerHabitDetail: React.FC<DrawerHabitDetailProps> = ({
  habit,
  isOpen,
  onClose,
  onSave,
  onDelete
}) => {
  const [localHabit, setLocalHabit] = useState<HabitEntity | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | undefined>();
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const { activeTags, createNewTag, getHabitTagIds, setHabitTagIds } = useTags();

  const drawerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevHabitIdRef = useRef<string | null>(null);

  const clearSuccessMessage = useCallback(() => {
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    successTimeoutRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  }, []);

  const performSave = useCallback(async (h: HabitEntity) => {
    try {
      setSaveStatus('saving');
      setSaveError(undefined);
      await Promise.resolve(onSave?.(h));

      setSaveStatus('success');
      clearSuccessMessage();
    } catch (error) {
      setSaveStatus('error');
      setSaveError(error instanceof Error ? error.message : '保存に失敗しました');
    }
  }, [onSave, clearSuccessMessage]);

  const debouncedSave = useCallback((h: HabitEntity) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setSaveStatus('dirty');
    debounceTimerRef.current = setTimeout(() => performSave(h), 500);
  }, [performSave]);

  // 同期: 親から受け取った habit が変わったらローカルに反映
  useEffect(() => {
    const currentId = habit?.id ?? null;
    const hasChanged = prevHabitIdRef.current !== currentId;

    if (hasChanged || !localHabit) {
      if (habit) {
        setLocalHabit({ ...habit });
        prevHabitIdRef.current = currentId;
      } else {
        setLocalHabit(null);
        prevHabitIdRef.current = null;
      }
      setSaveStatus('idle');
      setSaveError(undefined);
    }
  }, [habit, localHabit]);

  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (isOpen && drawerRef.current) drawerRef.current.scrollTop = 0;
  }, [isOpen]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!localHabit || isShoppingHabitId(localHabit.id)) return;
    const updated: HabitEntity = {
      ...localHabit,
      title: e.target.value,
      modified_at: new Date().toISOString()
    };
    setLocalHabit(updated);
    debouncedSave(updated);
  };

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!localHabit) return;
    const updated: HabitEntity = {
      ...localHabit,
      body: e.target.value,
      modified_at: new Date().toISOString()
    };
    setLocalHabit(updated);
    debouncedSave(updated);
  };

  const handleDelete = () => {
    if (!localHabit || isShoppingHabitId(localHabit.id)) return;
    onDelete?.(localHabit.id);
  };

  const handleTagSelectionChange = (tagIds: string[]) => {
    if (!localHabit) return;
    setSelectedTagIds(tagIds);
    if (localHabit.id) {
      setHabitTagIds(localHabit.id, tagIds);
    }
  };

  const handleAddNewTag = async (title: string) => {
    const newTag = await createNewTag(title);
    if (!selectedTagIds.includes(newTag.id)) {
      handleTagSelectionChange([...selectedTagIds, newTag.id]);
    }
  };

  useEffect(() => {
    if (!localHabit?.id) {
      setSelectedTagIds([]);
      return;
    }
    setSelectedTagIds(getHabitTagIds(localHabit.id));
  }, [localHabit?.id, getHabitTagIds]);

  return (
    <div className={`drawer-habit-detail ${isOpen ? 'drawer-habit-detail--open' : ''}`}>
      <div
        ref={drawerRef}
        className="drawer-habit-detail__drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-habit-title"
      >
        {localHabit && (
          <>
            <div className="drawer-habit-detail__header">
              <div className="drawer-habit-detail__header-left">
                <IconButton
                  icon="close"
                  onClick={onClose}
                  aria-label="閉じる"
                  tooltip="閉じる"
                  tooltipPosition="bottom-right"
                />
              </div>
              <div className="drawer-habit-detail__header-center">
                <SaveStatusFeedback status={saveStatus} errorMessage={saveError} />
              </div>
              <div className="drawer-habit-detail__header-right">
                {!isShoppingHabitId(localHabit.id) && (
                  <MenuButton
                    icon="menu"
                    aria-label="習慣操作メニュー"
                    tooltip="習慣操作メニュー"
                    tooltipPosition="bottom-left"
                    items={[{
                      type: 'Destructive',
                      label: '習慣を削除',
                      leftIcon: 'delete',
                      showLeftIcon: true,
                      onClick: handleDelete
                    }]}
                  />
                )}
              </div>
            </div>

            <div className="drawer-habit-detail__content">
              <div className="drawer-habit-detail__field">
                {isShoppingHabitId(localHabit.id) ? (
                  <div className="drawer-habit-detail__title drawer-habit-detail__title--disabled text-single-large">
                    {localHabit.title}
                  </div>
                ) : (
                  <InputText
                    hasLeftIcon={true}
                    leftIcon="habit"
                    placeholder="習慣名を入力"
                    value={localHabit.title}
                    onChange={handleTitleChange}
                  />
                )}
              </div>
              <div className="drawer-habit-detail__field">
                <InputTodoTags
                  selectedTagIds={selectedTagIds}
                  availableTags={activeTags}
                  isOpen={isOpen}
                  autoFocusInput={false}
                  onTagSelectionChange={handleTagSelectionChange}
                  onAddNewTag={handleAddNewTag}
                />
              </div>
              <div className="drawer-habit-detail__field">
                <InputTextArea
                  placeholder="説明を入力（任意）"
                  value={localHabit.body || ''}
                  onChange={handleBodyChange}
                  rows={6}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DrawerHabitDetail;
